import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from './supabase'

// ──────────────────────────────────────────────────────────────────
// Servicio Express — capa de datos (Emilius Pizza, restaurant_id 27).
//
// Reglas que este archivo hace cumplir, en un solo lugar:
//   • Un pedido "web" es el que trae source='web' — el valor explícito
//     que escribe el checkout del menú público. NUNCA se deduce por
//     delivery_fee, dirección ni teléfono.
//   • Un pedido "express" es delivery_type='delivery'. Un envío de ₡0
//     TAMBIÉN cuenta y se puede asignar.
//   • Los pedidos anulados se excluyen con la MISMA lista de estados que
//     ya usa el resto del proyecto (DeliveryMsExpressSection / cierre de
//     caja). No se inventan nombres de estado.
//   • Asignar y marcar pagado NO escriben directo contra las tablas:
//     pasan por las RPCs atómicas, que resuelven concurrencia,
//     idempotencia y aislamiento por restaurante.
// ──────────────────────────────────────────────────────────────────

/** Estados que significan "pedido anulado". Misma lista que ya usa el proyecto. */
export const EXPRESS_EXCLUDED_STATUSES = [
  'cancelled',
  'canceled',
  'cancelado',
  'refunded',
  'reembolsado',
  'voided',
  'rejected',
  'removed',
  'deleted',
] as const

export function isVoidedStatus(status: string | null | undefined): boolean {
  return (EXPRESS_EXCLUDED_STATUSES as readonly string[]).includes(
    String(status || '')
      .toLowerCase()
      .trim(),
  )
}

/**
 * Quién puede ver DINERO en el módulo Express (montos a pagar, total
 * vendido, reporte de pagos y el botón de marcar pagado).
 *
 * Solo administración: dueño y administradores. Cualquier otro rol
 * —incluido el rol acotado "Mesas y Menú"— ve la operación (pedidos,
 * direcciones, repartidor asignado) pero NUNCA cifras de dinero.
 */
export function canSeeExpressMoney(role: string | null | undefined): boolean {
  return ['owner', 'admin', 'super_admin'].includes(
    String(role || '')
      .toLowerCase()
      .trim(),
  )
}

/** Código de Postgres cuando la tabla todavía no existe (falta correr el SQL). */
const MISSING_TABLE_CODE = '42P01'

export function isSetupPendingError(error: any): boolean {
  const code = String(error?.code || '')
  const msg = String(error?.message || '').toLowerCase()
  return (
    code === MISSING_TABLE_CODE ||
    (msg.includes('express_couriers') && msg.includes('does not exist')) ||
    (msg.includes('express_deliveries') && msg.includes('does not exist')) ||
    msg.includes('could not find the function public.express_assign_delivery_v1')
  )
}

export const SETUP_PENDING_MESSAGE =
  'Falta un paso: corré SETUP_EMILIUS_SERVICIO_EXPRESS.sql en Supabase para habilitar el Servicio Express.'

// ─── Tipos ────────────────────────────────────────────────────────

export interface ExpressCourier {
  id: number
  restaurant_id: number
  name: string
  phone: string | null
  is_active: boolean
  created_at: string
}

export interface ExpressDelivery {
  id: number
  restaurant_id: number
  order_id: number
  courier_id: number
  courier_name: string
  payout_amount: number
  order_number: string | null
  delivery_address: string | null
  order_created_at: string | null
  assigned_at: string
  paid_at: string | null
  is_active: boolean
}

export interface ExpressOrder {
  id: number
  order_number: string | null
  customer_name: string | null
  customer_phone: string | null
  delivery_type: string | null
  delivery_address: string | null
  maps_url: string | null
  delivery_lat: number | null
  delivery_lng: number | null
  delivery_zone: string | null
  delivery_fee: number
  total: number
  /** 'cash' | 'sinpe' | 'card' | 'online' … Define si el repartidor cobra. */
  payment_method: string | null
  status: string | null
  source: string | null
  created_at: string
  /** Nombre escrito a mano en el flujo anterior. Solo lectura acá. */
  delivery_driver_name: string | null
}

/** Un pedido a domicilio junto con su asignación activa, si tiene. */
export interface ExpressAssignmentRow {
  order: ExpressOrder
  delivery: ExpressDelivery | null
}

// ─── Rango de fechas en hora de Costa Rica ────────────────────────
// Los filtros entregan YYYY-MM-DD del calendario CR; acá se convierten
// en límites absolutos con offset -06:00 para que la consulta cubra
// exactamente ese día en Costa Rica, sin importar la zona del navegador.

/** El día de HOY según el calendario de Costa Rica, en formato YYYY-MM-DD. */
export function todayCrIso(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Costa_Rica',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

export function crRangeBounds(startDate: string, endDate: string) {
  return {
    fromIso: `${startDate}T00:00:00-06:00`,
    toIso: `${endDate}T23:59:59.999-06:00`,
  }
}

const ORDER_COLS =
  'id, order_number, customer_name, customer_phone, delivery_type, delivery_address, maps_url, delivery_lat, delivery_lng, delivery_zone, delivery_fee, total, payment_method, status, source, created_at, delivery_driver_name'

function normalizeOrder(row: any): ExpressOrder {
  return {
    id: Number(row.id),
    order_number: row.order_number ?? null,
    customer_name: row.customer_name ?? null,
    customer_phone: row.customer_phone ?? null,
    delivery_type: row.delivery_type ?? null,
    delivery_address: row.delivery_address ?? null,
    maps_url: row.maps_url ?? null,
    delivery_lat: row.delivery_lat ?? null,
    delivery_lng: row.delivery_lng ?? null,
    delivery_zone: row.delivery_zone ?? null,
    delivery_fee: Number(row.delivery_fee) || 0,
    total: Number(row.total) || 0,
    payment_method: row.payment_method ?? null,
    status: row.status ?? null,
    source: row.source ?? null,
    created_at: row.created_at,
    delivery_driver_name: row.delivery_driver_name ?? null,
  }
}

function normalizeDelivery(row: any): ExpressDelivery {
  return {
    id: Number(row.id),
    restaurant_id: Number(row.restaurant_id),
    order_id: Number(row.order_id),
    courier_id: Number(row.courier_id),
    courier_name: row.courier_name || '',
    payout_amount: Number(row.payout_amount) || 0,
    order_number: row.order_number ?? null,
    delivery_address: row.delivery_address ?? null,
    order_created_at: row.order_created_at ?? null,
    assigned_at: row.assigned_at,
    paid_at: row.paid_at ?? null,
    is_active: row.is_active !== false,
  }
}

// ─── Pedidos del menú público (pestaña "Pedidos Web") ─────────────

export function useWebOrders(
  restaurantId: number | string | undefined,
  startDate: string,
  endDate: string,
) {
  const [orders, setOrders] = useState<ExpressOrder[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchOrders = useCallback(async () => {
    if (!restaurantId) return
    setLoading(true)
    setError(null)
    try {
      const { fromIso, toIso } = crRangeBounds(startDate, endDate)
      // source='web' es el valor EXPLÍCITO que escribe el checkout del
      // menú público (/r/<slug>). Mesero escribe 'waiter' y la venta
      // rápida de caja escribe 'pos_quick_sale', así que no hay mezcla.
      const { data, error: err } = await supabase
        .from('orders')
        .select(ORDER_COLS)
        .eq('restaurant_id', Number(restaurantId))
        .eq('source', 'web')
        .gte('created_at', fromIso)
        .lte('created_at', toIso)
        .order('created_at', { ascending: false })
      if (err) throw err
      setOrders((data || []).map(normalizeOrder))
    } catch (e: any) {
      console.error('[Express] web orders error:', e)
      setError(e?.message || 'No se pudieron cargar los pedidos web.')
      setOrders([])
    } finally {
      setLoading(false)
    }
  }, [restaurantId, startDate, endDate])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  // Refresco en vivo: los pedidos entran solos sin recargar la página.
  useEffect(() => {
    if (!restaurantId) return
    const id = window.setInterval(fetchOrders, 30000)
    return () => window.clearInterval(id)
  }, [restaurantId, fetchOrders])

  return { orders, loading, error, refetch: fetchOrders }
}

// ─── Repartidores ─────────────────────────────────────────────────

export function useExpressCouriers(restaurantId: number | string | undefined) {
  const [couriers, setCouriers] = useState<ExpressCourier[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [setupPending, setSetupPending] = useState(false)

  const fetchCouriers = useCallback(async () => {
    if (!restaurantId) return
    setLoading(true)
    setError(null)
    try {
      const { data, error: err } = await supabase
        .from('express_couriers')
        .select('id, restaurant_id, name, phone, is_active, created_at')
        .eq('restaurant_id', Number(restaurantId))
        .order('is_active', { ascending: false })
        .order('name', { ascending: true })
      if (err) throw err
      setSetupPending(false)
      setCouriers(
        (data || []).map((r: any) => ({
          id: Number(r.id),
          restaurant_id: Number(r.restaurant_id),
          name: r.name || '',
          phone: r.phone ?? null,
          is_active: r.is_active !== false,
          created_at: r.created_at,
        })),
      )
    } catch (e: any) {
      if (isSetupPendingError(e)) {
        setSetupPending(true)
        setCouriers([])
      } else {
        console.error('[Express] couriers error:', e)
        setError(e?.message || 'No se pudieron cargar los repartidores.')
      }
    } finally {
      setLoading(false)
    }
  }, [restaurantId])

  useEffect(() => {
    fetchCouriers()
  }, [fetchCouriers])

  const activeCouriers = useMemo(
    () => couriers.filter((c) => c.is_active),
    [couriers],
  )

  return {
    couriers,
    activeCouriers,
    loading,
    error,
    setupPending,
    refetch: fetchCouriers,
  }
}

export async function createExpressCourier(input: {
  restaurantId: number | string
  name: string
  phone?: string | null
}): Promise<{ success: boolean; error?: string }> {
  const name = input.name.trim()
  if (!name) return { success: false, error: 'El nombre es obligatorio.' }
  const { error } = await supabase.from('express_couriers').insert({
    restaurant_id: Number(input.restaurantId),
    name,
    phone: input.phone?.trim() || null,
  })
  if (error) {
    if (isSetupPendingError(error))
      return { success: false, error: SETUP_PENDING_MESSAGE }
    if (String(error.code) === '23505')
      return {
        success: false,
        error: `Ya existe un repartidor activo llamado "${name}".`,
      }
    return { success: false, error: error.message }
  }
  return { success: true }
}

export async function updateExpressCourier(input: {
  id: number
  name: string
  phone?: string | null
}): Promise<{ success: boolean; error?: string }> {
  const name = input.name.trim()
  if (!name) return { success: false, error: 'El nombre es obligatorio.' }
  const { error } = await supabase
    .from('express_couriers')
    .update({
      name,
      phone: input.phone?.trim() || null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', input.id)
  if (error) {
    if (String(error.code) === '23505')
      return {
        success: false,
        error: `Ya existe un repartidor activo llamado "${name}".`,
      }
    return { success: false, error: error.message }
  }
  return { success: true }
}

/**
 * Activa o desactiva un repartidor. NUNCA se borra: el historial y los
 * reportes del periodo conservan su nombre.
 */
export async function setExpressCourierActive(
  id: number,
  isActive: boolean,
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('express_couriers')
    .update({ is_active: isActive, updated_at: new Date().toISOString() })
    .eq('id', id)
  if (error) return { success: false, error: error.message }
  return { success: true }
}

// ─── Entregas a domicilio + su asignación ─────────────────────────

export function useExpressDeliveries(
  restaurantId: number | string | undefined,
  startDate: string,
  endDate: string,
) {
  const [rows, setRows] = useState<ExpressAssignmentRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [setupPending, setSetupPending] = useState(false)

  const fetchRows = useCallback(async () => {
    if (!restaurantId) return
    setLoading(true)
    setError(null)
    try {
      const { fromIso, toIso } = crRangeBounds(startDate, endDate)
      // Los express se identifican por TIPO DE ENTREGA, no por tarifa:
      // un envío de ₡0 también se asigna y cuenta como entrega.
      const { data: orderRows, error: orderErr } = await supabase
        .from('orders')
        .select(ORDER_COLS)
        .eq('restaurant_id', Number(restaurantId))
        .eq('delivery_type', 'delivery')
        .gte('created_at', fromIso)
        .lte('created_at', toIso)
        .order('created_at', { ascending: false })
      if (orderErr) throw orderErr

      const orders = (orderRows || [])
        .map(normalizeOrder)
        .filter((o) => !isVoidedStatus(o.status))

      if (orders.length === 0) {
        setRows([])
        setSetupPending(false)
        return
      }

      const { data: deliveryRows, error: deliveryErr } = await supabase
        .from('express_deliveries')
        .select('*')
        .eq('restaurant_id', Number(restaurantId))
        .eq('is_active', true)
        .in(
          'order_id',
          orders.map((o) => o.id),
        )
      if (deliveryErr) {
        if (isSetupPendingError(deliveryErr)) {
          setSetupPending(true)
          setRows(orders.map((order) => ({ order, delivery: null })))
          return
        }
        throw deliveryErr
      }

      setSetupPending(false)
      const byOrder = new Map<number, ExpressDelivery>()
      for (const r of deliveryRows || []) {
        const d = normalizeDelivery(r)
        byOrder.set(d.order_id, d)
      }
      setRows(
        orders.map((order) => ({
          order,
          delivery: byOrder.get(order.id) || null,
        })),
      )
    } catch (e: any) {
      console.error('[Express] deliveries error:', e)
      setError(e?.message || 'No se pudieron cargar las entregas.')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [restaurantId, startDate, endDate])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  return { rows, loading, error, setupPending, refetch: fetchRows }
}

/**
 * Asigna (o reasigna) un pedido a un repartidor mediante la RPC atómica.
 * La RPC toma el monto y la dirección del propio pedido y guarda un
 * snapshot inmutable — el reporte histórico no cambia después.
 */
export async function assignExpressDelivery(input: {
  restaurantId: number | string
  orderId: number
  courierId: number
  idempotencyKey: string
}): Promise<{ success: boolean; error?: string; conflict?: boolean }> {
  const { error } = await supabase.rpc('express_assign_delivery_v1', {
    p_restaurant_id: Number(input.restaurantId),
    p_order_id: input.orderId,
    p_courier_id: input.courierId,
    p_idempotency_key: input.idempotencyKey,
  })
  if (error) {
    if (isSetupPendingError(error))
      return { success: false, error: SETUP_PENDING_MESSAGE }
    // 23505 = otro usuario asignó este mismo pedido al mismo tiempo.
    if (String(error.code) === '23505' || String(error.code) === '40001') {
      return {
        success: false,
        conflict: true,
        error:
          'Otra persona acaba de asignar este pedido. Actualizamos la lista para que veas a quién quedó.',
      }
    }
    return { success: false, error: error.message }
  }
  // Asignado = el pedido salió. Se persiste en la base para que el
  // estado sobreviva a recargas y lo vean todos los dispositivos.
  await markOrderDispatched(input.orderId)
  return { success: true }
}

/**
 * Quita el repartidor de un pedido y lo devuelve a "pendiente de asignar".
 *
 * La asignación NO se borra: se archiva (is_active = false) igual que
 * cuando se reasigna, así el historial queda intacto. El pedido vuelve a
 * un estado previo al despacho para que la lista de Pedidos Web y el
 * KDS queden de acuerdo entre sí.
 */
export async function unassignExpressDelivery(input: {
  restaurantId: number | string
  orderId: number
  deliveryId: number
}): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('express_deliveries')
    .update({ is_active: false, superseded_at: new Date().toISOString() })
    .eq('id', input.deliveryId)
    .eq('restaurant_id', Number(input.restaurantId))
    .eq('is_active', true)
  if (error) {
    if (isSetupPendingError(error))
      return { success: false, error: SETUP_PENDING_MESSAGE }
    console.error('[Express] unassign error:', error)
    return {
      success: false,
      error: error.message || 'No se pudo quitar el repartidor.',
    }
  }

  // El pedido deja de estar despachado. Se limpian los campos que
  // alimentan la sección Express de Métricas para que no quede un
  // repartidor "fantasma" en los reportes antiguos.
  const { error: orderError } = await supabase
    .from('orders')
    .update({
      delivery_driver_name: null,
      delivery_paid_to_driver: false,
      delivery_paid_at: null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', input.orderId)
  if (orderError) {
    console.warn('[Express] no se pudo limpiar el repartidor del pedido:', orderError)
  }

  await revertOrderDispatch(input.orderId)
  return { success: true }
}

/**
 * Devuelve un pedido despachado a "en preparación".
 *
 * Solo actúa sobre los estados que produce el despacho
 * ('out_for_delivery' / 'ready'): un pedido ya entregado, anulado o que
 * todavía no salió NO se toca. Se usa 'preparing' a propósito y no
 * 'new', para no volver a disparar la alerta de pedido nuevo en el KDS.
 */
async function revertOrderDispatch(orderId: number): Promise<void> {
  const { error } = await supabase
    .from('orders')
    .update({ status: 'preparing', updated_at: new Date().toISOString() })
    .eq('id', orderId)
    .in('status', ['out_for_delivery', 'ready'])
  if (error) {
    console.warn('[Express] no se pudo revertir el estado del pedido:', error)
  }
}

/**
 * Marca un pedido como despachado ("Enviado") al asignarle repartidor.
 *
 * NO BLOQUEANTE a propósito: si el estado no se puede escribir (por
 * ejemplo si la base restringe los valores permitidos), la asignación
 * ya quedó guardada y no tiene sentido revertirla ni mostrar un error.
 * Se intenta 'out_for_delivery' y, si la base lo rechaza, se cae a
 * 'ready', que es un estado usado desde siempre en el proyecto.
 */
async function markOrderDispatched(orderId: number): Promise<void> {
  const trySet = async (status: string) => {
    const { error } = await supabase
      .from('orders')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', orderId)
      // Nunca se pisa un pedido ya entregado o anulado.
      .not('status', 'in', '("delivered","cancelled")')
    return error
  }
  const err = await trySet('out_for_delivery')
  if (!err) return
  // 23514 = check constraint: ese valor de estado no está permitido.
  if (String(err.code) === '23514') {
    await trySet('ready')
    return
  }
  console.warn('[Express] no se pudo marcar el pedido como enviado:', err)
}

/**
 * Marca un pedido de Recoger / Comer en el local como LISTO.
 * Se guarda en la base (orders.status = 'ready').
 */
export async function markWebOrderReady(
  orderId: number,
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('orders')
    .update({ status: 'ready', updated_at: new Date().toISOString() })
    .eq('id', orderId)
    .not('status', 'in', '("delivered","cancelled")')
  if (error) {
    console.error('[Express] mark ready error:', error)
    return {
      success: false,
      error: error.message || 'No se pudo marcar el pedido como listo.',
    }
  }
  return { success: true }
}

/** Marca (o desmarca) una entrega como pagada al repartidor. Idempotente. */
export async function setExpressDeliveryPaid(input: {
  restaurantId: number | string
  deliveryId: number
  paid: boolean
}): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase.rpc('express_set_delivery_paid_v1', {
    p_restaurant_id: Number(input.restaurantId),
    p_delivery_id: input.deliveryId,
    p_paid: input.paid,
  })
  if (error) {
    if (isSetupPendingError(error))
      return { success: false, error: SETUP_PENDING_MESSAGE }
    return { success: false, error: error.message }
  }
  return { success: true }
}

/**
 * Mapa order_id → nombre del repartidor asignado, para mostrar la
 * etiqueta en el KDS. Solo lectura, una consulta por refresco.
 *
 * Si la tabla todavía no existe (falta correr el SQL) devuelve un mapa
 * vacío y NO propaga error: el KDS nunca se rompe por esto.
 */
export function useActiveExpressAssignments(
  restaurantId: number | string | undefined,
  enabled: boolean,
) {
  const [byOrderId, setByOrderId] = useState<Map<number, string>>(new Map())

  const fetchAssignments = useCallback(async () => {
    if (!restaurantId || !enabled) return
    try {
      const { data, error } = await supabase
        .from('express_deliveries')
        .select('order_id, courier_name')
        .eq('restaurant_id', Number(restaurantId))
        .eq('is_active', true)
      if (error) throw error
      const next = new Map<number, string>()
      for (const row of data || []) {
        next.set(Number((row as any).order_id), (row as any).courier_name || '')
      }
      setByOrderId(next)
    } catch (e) {
      // Silencioso a propósito: el KDS es pantalla de cocina.
      if (!isSetupPendingError(e)) {
        console.warn('[Express] KDS assignments lookup failed:', e)
      }
      setByOrderId(new Map())
    }
  }, [restaurantId, enabled])

  useEffect(() => {
    fetchAssignments()
  }, [fetchAssignments])

  useEffect(() => {
    if (!enabled || !restaurantId) return
    const id = window.setInterval(fetchAssignments, 30000)
    return () => window.clearInterval(id)
  }, [enabled, restaurantId, fetchAssignments])

  return byOrderId
}

// ─── Reporte por repartidor ───────────────────────────────────────

export interface CourierReportRow {
  courierId: number | null
  courierName: string
  deliveries: number
  total: number
  paid: number
  pending: number
  /** Historial escrito a mano, sin vincular al catálogo. */
  legacy: boolean
  rows: ExpressAssignmentRow[]
}

/**
 * Agrupa las entregas del rango por repartidor. Los montos salen del
 * SNAPSHOT de la asignación, no del pedido: si el costo de envío se
 * corrigió después, el reporte no cambia.
 */
export function buildCourierReport(
  rows: ExpressAssignmentRow[],
): CourierReportRow[] {
  const map = new Map<string, CourierReportRow>()

  for (const row of rows) {
    const { delivery, order } = row
    let key: string
    let name: string
    let courierId: number | null
    let legacy = false

    const handwritten = (order.delivery_driver_name || '').trim()

    if (delivery) {
      key = `c:${delivery.courier_id}`
      name = delivery.courier_name
      courierId = delivery.courier_id
    } else if (handwritten) {
      // Historial anterior: el nombre quedó escrito a mano y no está
      // vinculado al catálogo. Se muestra igual, marcado como tal.
      key = `legacy:${handwritten.toLowerCase()}`
      name = handwritten
      courierId = null
      legacy = true
    } else {
      continue // pedido sin repartidor todavía
    }

    const existing = map.get(key)
    // El monto sale del SNAPSHOT de la asignación. Para el historial
    // escrito a mano no hay snapshot, así que se usa el costo de envío
    // del pedido — es la única referencia disponible.
    const amount = delivery ? delivery.payout_amount : order.delivery_fee
    const isPaid = delivery ? delivery.paid_at !== null : false

    if (existing) {
      existing.deliveries += 1
      existing.total += amount
      if (isPaid) existing.paid += amount
      else existing.pending += amount
      existing.rows.push(row)
    } else {
      map.set(key, {
        courierId,
        courierName: name,
        deliveries: 1,
        total: amount,
        paid: isPaid ? amount : 0,
        pending: isPaid ? 0 : amount,
        legacy,
        rows: [row],
      })
    }
  }

  return Array.from(map.values()).sort((a, b) => b.total - a.total)
}
